/**
 * Extract lỗi BE chuẩn từ axios error.
 * Spec API ERP-docs: { success: false, status: number, error: { code, message, details? } }.
 * Một path duy nhất — không fallback. Caller phải truyền err vào catch trực tiếp.
 *
 * Generic D cho phép caller khai báo shape của details mà không cần cast tại point of use:
 *   getApiError<{ current: number; limit: number }>(err)?.details?.current
 */

interface ApiErrorBody<D> {
  code?: string;
  message?: string;
  details?: D;
}

interface AxiosErrorShape<D> {
  response?: {
    data?: {
      // Envelope chuẩn BE: { success:false, error:{...} }
      error?: ApiErrorBody<D> | string;
      // NestJS default: { statusCode, message, error }
      message?: string | string[];
      statusCode?: number;
      details?: D;
      code?: string;
    };
  };
}

export function getApiError<D = Record<string, unknown>>(err: unknown): ApiErrorBody<D> | null {
  const data = (err as AxiosErrorShape<D>)?.response?.data;
  if (!data) return null;

  // 1) Envelope chuẩn
  if (data.error && typeof data.error === 'object') {
    return data.error as ApiErrorBody<D>;
  }

  // 2) error là string
  if (typeof data.error === 'string') {
    return { message: data.error, details: data.details };
  }

  // 3) NestJS: message có thể là string[]
  if (Array.isArray(data.message) && data.message.length > 0) {
    return {
      code: data.code ?? String(data.statusCode ?? ''),
      message: data.message.join('; '),
      details: data.details,
    };
  }

  if (typeof data.message === 'string' && data.message.trim()) {
    return {
      code: data.code ?? String(data.statusCode ?? ''),
      message: data.message,
      details: data.details,
    };
  }

  return null;
}
